/* ==========================================================================
   <mood-switcher> — "What's your mood today?"
   WAI-ARIA tabs (automatic activation, roving tabindex, arrows/Home/End, RTL aware).
   Other moods load lazily via Section Rendering (`mood-switcher-products` rendered
   on /collections/<handle>) and are cached per session in memory.

   Personalization foundation: the last chosen mood key (a handleized label, never
   personal data) is stored on-device in SIM.store('mood') and preselected next visit.
   Future: for logged-in customers, map moods to a customer metafield
   (e.g. customer.metafields.custom.preferred_mood) rendered into data-preferred on
   this element, and let it take precedence over the device value. A 'mood:change'
   event is emitted so other sections (hero, recommendations) can react.
   ========================================================================== */
const SIM = window.SIM || {};

if (!customElements.get('mood-switcher')) {
  class MoodSwitcher extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (!this.tabs.length) return;
      this.panel = this.querySelector('[role="tabpanel"]');
      this.stage = this.querySelector('[data-mood-stage]');
      this.list = this.querySelector('[data-mood-list]');
      this.link = this.querySelector('[data-mood-link]');
      this.linkLabel = this.querySelector('[data-mood-link-label]');
      this.status = this.querySelector('[data-mood-status]');
      this.rail = this.querySelector('product-rail');
      this.limit = Number(this.dataset.limit || 4);
      this.remember = this.dataset.remember === 'true';
      this.cache = new Map();
      this.token = 0;
      this.current = this.tabs.find((t) => t.getAttribute('aria-selected') === 'true') || this.tabs[0];
      this.cache.set(this.current.dataset.block, this.list.innerHTML);

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab));
      });
      this.querySelector('[role="tablist"]').addEventListener('keydown', (e) => this.onKey(e));

      // Warm the cache on intent (hover/focus) so the swap feels instant
      this.tabs.forEach((tab) => {
        const warm = () => { this.load(tab).catch(() => {}); };
        tab.addEventListener('pointerenter', warm, { once: true });
      });

      this.preselect();

      this.onBlockSelect = (e) => {
        const tab = this.tabs.find((t) => t.dataset.block === e.detail.blockId);
        if (tab) this.select(tab, { persist: false });
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    preselect() {
      if (!this.remember || window.Shopify?.designMode || !SIM.store) return;
      const saved = SIM.store.get('mood');
      if (typeof saved !== 'string') return;
      const tab = this.tabs.find((t) => t.dataset.key === saved);
      if (tab && tab !== this.current) this.select(tab, { persist: false, scroll: false });
    }

    onKey(e) {
      const i = this.tabs.indexOf(e.target.closest('[role="tab"]'));
      if (i < 0) return;
      const rtl = getComputedStyle(this).direction === 'rtl';
      const len = this.tabs.length;
      let n;
      switch (e.key) {
        case 'ArrowRight': n = rtl ? i - 1 : i + 1; break;
        case 'ArrowLeft': n = rtl ? i + 1 : i - 1; break;
        case 'Home': n = 0; break;
        case 'End': n = len - 1; break;
        default: return;
      }
      e.preventDefault();
      this.select(this.tabs[(n + len) % len], { focus: true });
    }

    template(name) {
      const tpl = this.querySelector(`template[data-mood-${name}]`);
      return tpl ? tpl.innerHTML : '';
    }

    async load(tab) {
      const id = tab.dataset.block;
      if (this.cache.has(id)) return this.cache.get(id);
      const handle = tab.dataset.handle;
      if (!handle) {
        const html = this.template('placeholder');
        this.cache.set(id, html);
        return html;
      }
      const root = (SIM.config?.routes?.root_url || '/').replace(/\/$/, '');
      const pending = SIM.fetchSectionHTML(`${root}/collections/${encodeURIComponent(handle)}`, 'mood-switcher-products')
        .then((html) => {
          const doc = SIM.parseHTML(html);
          const items = Array.from(doc.querySelectorAll('[data-mood-products] > li')).slice(0, this.limit);
          const out = items.length ? items.map((li) => li.outerHTML).join('') : this.template('empty');
          this.cache.set(id, out);
          return out;
        })
        .catch((err) => {
          this.cache.delete(id);
          throw err;
        });
      this.cache.set(id, pending);
      return pending;
    }

    fadeOut() {
      if (!SIM.motionOK?.()) return Promise.resolve();
      this.stage.classList.add('is-fading');
      return new Promise((r) => setTimeout(r, 240));
    }

    async select(tab, { focus = false, persist = true, scroll = true } = {}) {
      if (focus) tab.focus();
      if (scroll) this.scrollTabIntoView(tab);
      if (tab === this.current) return;

      this.tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
      });
      this.current = tab;
      this.panel.setAttribute('aria-labelledby', tab.id);
      const token = ++this.token;
      this.panel.setAttribute('aria-busy', 'true');

      const fade = this.fadeOut();
      let html;
      try { html = await this.load(tab); } catch { html = this.template('error'); }
      await fade;
      if (token !== this.token) return;

      this.list.innerHTML = html;
      this.list.scrollLeft = 0;
      this.querySelectorAll('[data-mood-intro]').forEach((el) => {
        el.hidden = el.dataset.moodIntro !== tab.dataset.block;
      });
      if (this.link) this.link.href = tab.dataset.url;
      if (this.linkLabel) this.linkLabel.textContent = tab.dataset.shopLabel;

      requestAnimationFrame(() => this.stage.classList.remove('is-fading'));
      this.panel.removeAttribute('aria-busy');
      if (this.status) this.status.textContent = tab.dataset.status;
      this.rail?.update?.();
      SIM.initReveal?.(this.stage);

      if (persist && this.remember) SIM.store?.set('mood', tab.dataset.key);
      SIM.emit?.('mood:change', { key: tab.dataset.key, handle: tab.dataset.handle });
    }

    scrollTabIntoView(tab) {
      const wrap = tab.closest('.mood__tabs-wrap');
      if (!wrap || wrap.scrollWidth <= wrap.clientWidth) return;
      const w = wrap.getBoundingClientRect();
      const t = tab.getBoundingClientRect();
      const delta = t.left + t.width / 2 - (w.left + w.width / 2);
      wrap.scrollBy({ left: delta, behavior: SIM.motionOK?.() ? 'smooth' : 'auto' });
    }
  }
  customElements.define('mood-switcher', MoodSwitcher);
}
